import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map, catchError, filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { User } from '../models';
import { UtilsService } from 'src/app/shared/services';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { EventType } from '@azure/msal-browser';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private userSubject: BehaviorSubject<any>;
  public user: Observable<User>;
  public storage = localStorage;
  public emailForOtp = '';

  constructor(
    private router: Router,
    private http: HttpClient,
    private utilsService: UtilsService,
    private loaderService: LoaderService,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
  ) {
    this.userSubject = new BehaviorSubject<User>(
      JSON.parse(localStorage.getItem('user') || 'null')
    );
    this.user = this.userSubject.asObservable();
    this.listenToLogoutEvent();
  }

  public get userValue(): User {
    return this.userSubject.value;
  }

  updateToken(token: string){
    this.userSubject.next({token}); // Update BehaviorSubject
    const user = this.userValue; // Use userValue instead of user
    if (user) {
      user.token = token; // Assign token
      // user.isAdLogin = true;// for adlogin
    }
  }

  login(
    username: string,
    password: string,
    isOTP: boolean,
    rememberMe: boolean = false,
    isAdLogin: string = '',
  ) {
    if(!isAdLogin){
      // Enable/disable remember me functionality
      if (rememberMe) {
        localStorage.setItem('rememberMe', JSON.stringify(rememberMe));
        this.storage = localStorage;
      }
      localStorage.removeItem('pageSize');
      return this.http
        .post<User>(`${environment.authApiUrl}/v1/auth/login?isOTP=${isOTP}`, {
          mailId: username,
          password,
        })
        .pipe(
          map((u: any) => {
            this.loaderService.fnHideLoader();
            const token = u.jwtToken;
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            if (u.Success && u.Success.id) {
              const encryptedOTP = this.utilsService.getShareKeyString(password);
  
              this.storage.setItem(
                'user',
                JSON.stringify({ ...u.Success, token, isOTP, encryptedOTP })
              );
              this.userSubject.next({ ...u.Success, token, isOTP, password });
            } else {
              this.loaderService.fnHideLoader();
            }
  
            return u;
          }),
          catchError((err)=>{
            this.loaderService.fnHideLoader();
            return throwError(() => err);
          })
        )     
    }else{
      if (rememberMe) {
        localStorage.setItem('rememberMe', JSON.stringify(rememberMe));
        this.storage = localStorage;
      }
      const headers = new HttpHeaders({
        'Auth': 'B2C_1A_TRUMINDSSIGNIN'
      });
      localStorage.removeItem('pageSize');
      return this.http
        .post<User>(`${environment.authApiUrl}/v1/auth/login?isOTP=${isOTP}`,{}, { headers })
        .pipe(
          map((u: any) => {
            this.loaderService.fnHideLoader();
            const token = u.jwtToken;
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            if (u.Success && u.Success.id) {
              const encryptedOTP = this.utilsService.getShareKeyString(password);
  
              this.storage.setItem(
                'user',
                JSON.stringify({ ...u.Success, token, isOTP, encryptedOTP, isAdLogin: true })
              );
              this.userSubject.next({ ...u.Success, token, isOTP, password, isAdLogin: true });
            } else {
              this.loaderService.fnHideLoader();
            }
  
            return u;
          }),
          catchError((err)=>{
            this.loaderService.fnHideLoader();
            return throwError(() => err);
          })
        )
    }
  }

  forgotPassword(mail: string) {
    return this.http
      .post<User>(`${environment.authApiUrl}/v1/auth/reset-password`, { mail })
      .pipe(
        map((u: any) => {
          return u;
        })
      );
  }

  logout() {
    // remove user from local storage and set current user to null
    // session expired then call clearStorage and remove all Item after call logout api to make token expire
    const user = this.userValue;
    /** Azure ad logout */
    if(user && user.isAdLogin){
      this.logoutSSO()
      .catch((error) => {
        // fallback or cleanup logic if needed
        this.clearStorage();
        window.location.reload();
      });
    } else {
      /** truMIS user logout */
      const payload = {};
      this.http
        .post(`${environment.authApiUrl}/v1/auth/logout`, payload)
        .subscribe(() => {
          this.clearStorage();
        });
    }
  }

  logoutSSO = async () => {
    const msalInstance = this.msalService.instance; // Access MSAL instance
    if (msalInstance) {
      try {
        await msalInstance.initialize();
        msalInstance.clearCache();
  
        const account = msalInstance.getActiveAccount();
        await msalInstance.logoutRedirect({
          account,
          postLogoutRedirectUri: `${environment.msalConfig.postLogoutRedirectUri}`, // Ensure this returns a valid URI
        });
      } catch (err) {
        throw err;
      }
    } else {
      throw new Error('MSAL instance not available');
    }
  };

  /** Azure AD broadcast service to get logout response from Azure and on success, clear storage */
  listenToLogoutEvent() {
    // msal redirect to microsoft page
    this.msalBroadcastService.msalSubject$
      .pipe(filter(event => event.eventType === EventType.LOGOUT_SUCCESS))
      .subscribe(() => {
        this.clearStorage();
      });
  }

  clearStorage() {
    this.utilsService.dismissModalFade();// remove all modal backdrop fade
    localStorage.clear();
    this.userSubject.next({});
    this.router.navigate(['/auth/login']);
  }

  setIsOtp() {
    // remove user from local storage and set current user to null
    //localStorage.setItem('rememberMe', JSON.stringify(rememberMe));
    const newUser = { ...this.userValue, isOTP: false };
    localStorage.setItem('user', JSON.stringify(newUser));
    this.userSubject.next(newUser);
  }

  register(user: User) {
    return this.http.post(`${environment.authApiUrl}/auth/register`, user);
  }
}
